import { useQuery } from '@tanstack/react-query';
import { api, type ChatPeopleSearchHit } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/** Fewest characters before the directory is consulted (matches the route's floor). */
export const CHAT_PEOPLE_SEARCH_MIN_LENGTH = 2;

/** The text sent to the directory: trimmed, with the "@" handle sigil dropped. */
export function normalizePeopleSearchQuery(raw: string): string {
  return raw.trim().replace(/^@/, '');
}

/**
 * Directory search for people to message — username OR display-name prefix,
 * backed by `GET /arches/chat/users/search`. Each hit says whether a DM with
 * that person already exists. Shared by the New Chat dialog and the People
 * section of "Search all chats".
 *
 * The caller is responsible for debouncing `query`; the hook does not, so a
 * panel's one debounce governs every section it feeds. Email-like input is
 * never searched (an "@" after the first character means an address is being
 * typed, which the New Chat dialog resolves on submit instead).
 */
export function useChatPeopleSearch(
  query: string,
  enabled: boolean,
): {
  results: ChatPeopleSearchHit[];
  /** True when the query will be sent to the directory (the panel's own gate too). */
  isSearchable: boolean;
  isLoading: boolean;
  isError: boolean;
} {
  const normalized = normalizePeopleSearchQuery(query);
  const searchable =
    normalized.length >= CHAT_PEOPLE_SEARCH_MIN_LENGTH && !normalized.includes('@');

  const result = useQuery({
    queryKey: queryKeys.chat.peopleSearch(normalized),
    queryFn: () => api.searchUsersForDm(normalized),
    select: (data) => data.users,
    enabled: enabled && searchable,
    staleTime: 30_000,
  });

  return {
    results: result.data ?? [],
    isSearchable: searchable,
    isLoading: enabled && searchable && result.isLoading,
    isError: result.isError,
  };
}
